import React, { useState, useEffect, RefObject } from 'react';
import styled from 'styled-components';
import { gridConfig } from '@tvg/fuse-design';
import Button from 'components/Button/Button';
import debounce from '@sd-utility/debounce';

const Container = styled.div<{ containerIsVisible: boolean }>`
  visibility: hidden;
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  padding: var(--spacing-space24);
  background-color: white;
  transition: all 300ms ease-out;
  transform: translateY(100%);
  box-shadow:
    0px -3px 6px rgba(0, 0, 0, 0.15),
    0px -2px 4px rgba(0, 0, 0, 0.12);
  z-index: 10;

  ${({ containerIsVisible }) =>
    containerIsVisible &&
    `
    visibility: visible;
    transform: translateY(0%);
  `}

  @media (min-width: ${gridConfig.l.minWidth}) {
    display: none;
  }
`;

const lessThan100PxFromBottomOfPage = () => window.innerHeight + window.pageYOffset >= document.body.offsetHeight - 100;

interface Props {
  primaryAddToBasketButtonRef: RefObject<HTMLDivElement>;
  handleAddToBasket: (id: string, label: string) => void;
  disabled: boolean;
  updateBasket?: boolean;
}

function StickyAddToBasketButton({
  primaryAddToBasketButtonRef,
  handleAddToBasket,
  disabled,
  updateBasket = false,
}: Props) {
  const [containerIsVisible, setContainerIsVisible] = useState(false);

  const toggleStickyAddToBasketVisibility = debounce(() => {
    if (!primaryAddToBasketButtonRef.current) return;

    const { height, y } = primaryAddToBasketButtonRef.current.getBoundingClientRect();
    const scrolledAbovePrimaryAddToBasketButton = height > -y;

    if (scrolledAbovePrimaryAddToBasketButton || lessThan100PxFromBottomOfPage()) {
      setContainerIsVisible(false);
    } else {
      setContainerIsVisible(true);
    }
  }, 50);

  useEffect(() => {
    window.addEventListener('scroll', toggleStickyAddToBasketVisibility);

    return () => {
      window.removeEventListener('scroll', toggleStickyAddToBasketVisibility);
    };
  }, []);

  return (
    <Container containerIsVisible={containerIsVisible} data-testid="sticky_add_to_basket_container">
      <Button
        id="stickyAddToBasketButton"
        onClick={() => handleAddToBasket('stickyAddToBasketButton', 'Sticky add to basket')}
        data-testid="sticky_add_to_basket_button"
        disabled={disabled}
      >
        {updateBasket ? 'Update basket' : 'Add to basket'}
      </Button>
    </Container>
  );
}

export default StickyAddToBasketButton;
